import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface OnboardingSlide {
  icon: string;
  title: string;
  description: string;
  detail: string;
}

const slides: OnboardingSlide[] = [
  {
    icon: '\u2328',
    title: 'Sua digitacao e unica',
    description:
      'Assim como sua impressao digital, a forma como voce digita e impossivel de replicar. Nossa tecnologia proprietaria captura centenas de micro-padroes invisiveis para criar sua identidade digital.',
    detail:
      'O Seu Neurocomportamento motor unico, cria um perfil biométrico exclusívo - sem senhas, sem risco.',
  },
  {
    icon: '\u{1F9EA}',
    title: 'Cadastro rapido e simples',
    description:
      'Em apenas 5 sessões rapidas, Nós aprendemos quem você é. Cada sessão leva menos de 1 minuto — simples como digitar uma mensagem.',
    detail:
      'Apenas digite naturalmente. Nossa tecnologia faz o resto.',
  },
  {
    icon: '\u{1F512}',
    title: 'Voce e a senha',
    description:
      'Esqueca senhas e tokens. A partir de agora, voce e a sua propria autenticacao. Seu comportamento fala por você em tempo real, a cada movimento.',
    detail:
      'Autenticacao continua e invisivel. Seguranca maxima sem nenhum esforco.',
  },
];

/**
 * Onboarding page with 3 slides and stepper navigation.
 */
export default function Onboarding() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  const isLast = currentSlide === slides.length - 1;

  const handleNext = useCallback(() => {
    if (isLast) {
      navigate('/enrollment');
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  }, [isLast, navigate]);

  const handlePrev = useCallback(() => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  }, []);

  const handleSkip = useCallback(() => {
    navigate('/enrollment');
  }, [navigate]);

  const slide = slides[currentSlide];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      {/* Stepper */}
      <div className="flex items-center gap-2 mb-12">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentSlide(i)}
            className={`
              h-2 rounded-full transition-all duration-300
              ${i === currentSlide
                ? 'w-8 bg-accent-brand'
                : i < currentSlide
                  ? 'w-2 bg-accent-brand/50'
                  : 'w-2 bg-bg-surface'
              }
            `}
          />
        ))}
      </div>

      {/* Slide content */}
      <div className="max-w-lg text-center animate-fade-in" key={currentSlide}>
        <span className="text-6xl mb-8 block">{slide.icon}</span>

        <h2 className="text-2xl md:text-3xl font-bold text-txt-primary font-sans mb-4">
          {slide.title}
        </h2>

        <p className="text-base text-txt-muted font-sans leading-relaxed mb-4">
          {slide.description}
        </p>

        <p className={`text-sm font-sans leading-relaxed mb-8 px-4 py-3 bg-bg-surface/30 rounded-card border border-bg-surface/50 ${currentSlide === 1 ? 'text-amber-300' : 'text-txt-muted/80'}`}>
          {slide.detail}
        </p>

        {/* Metrics preview on first slide */}
        {currentSlide === 0 && (
          <div className="flex justify-center gap-4 mb-8">
            {[
              { label: 'Identidade', desc: 'Perfil unico' },
              { label: 'Precisao', desc: 'Física e Comportamento Avançado' },
              { label: 'Seguranca', desc: 'Impossivel de copiar' },
            ].map((metric, i) => (
              <div
                key={i}
                className="px-4 py-3 bg-bg-secondary border border-bg-surface rounded-card text-center"
              >
                <span className="block text-xs text-white font-mono font-medium">
                  {metric.label}
                </span>
                <span className="block text-[10px] text-amber-200 mt-0.5 font-sans">
                  {metric.desc}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Session preview on second slide */}
        {currentSlide === 1 && (
          <div className="flex justify-center gap-2 mb-8">
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="w-10 h-10 rounded-full border-2 border-bg-surface flex items-center justify-center text-sm font-mono text-txt-muted"
              >
                {i + 1}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between w-full max-w-lg mt-8">
        <button
          onClick={handlePrev}
          disabled={currentSlide === 0}
          className="text-sm text-txt-muted hover:text-txt-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-sans"
        >
          &larr; Anterior
        </button>

        <button
          onClick={handleSkip}
          className="text-sm text-txt-muted hover:text-txt-primary transition-colors font-sans"
        >
          Pular
        </button>

        <button
          onClick={handleNext}
          className="text-sm bg-accent-brand hover:bg-accent-brand/90 text-white px-6 py-2.5 rounded-lg font-sans font-medium transition-colors"
        >
          {isLast ? 'Comecar Cadastro' : 'Proximo'} &rarr;
        </button>
      </div>
    </div>
  );
}
